
import { MaterialIcon } from "components/ui/shared/MaterialIcon";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getTimelineDates, parseDuration } from "utilities/dateAndTime";

export const TasksTimeline = ({tasks, projects}) => {

    const timelineRef = useRef(null);
    const hasScrolledRef = useRef(false);

    const dates = getTimelineDates();

    const today = new Date();
    const dayOfWeek = today.getDay(); 
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

    today.setDate(today.getDate() + diff);

    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');

    const currentMonday = `${y}-${m}-${d}`;

    useEffect(() => {
        
        if(hasScrolledRef.current) return;
        if (!timelineRef.current) return;


        const mondayIndex = dates.findIndex(
            d => d.fullDate === currentMonday
        );

        if (mondayIndex === -1) return;

        const DAY_WIDTH = 70;

        timelineRef.current.scrollLeft = mondayIndex * DAY_WIDTH;

        hasScrolledRef.current = true;
    }, [dates, currentMonday]);

    const gridStructure = generateTimelineStructure(projects, tasks);
    const totalRows = Math.max(...gridStructure.map(p => p.grid.rowEnd)) -1;
    
    return (
        <StyledTasksTimeline ref={timelineRef} $totalRows={totalRows}>
            <div className="timelineEmpty"/>

            {dates.map((d) =>
                <div className="timelineDate" key={d.fullDate}>
                    <span className="month">{d.month}</span>
                    <span className={d.day === 'Søn' || d.day === 'Lør' ? "day weekend" : "day"}>{d.day}</span>
                    <span className="date">{d.date}</span>
                </div>
            )}

            {
                gridStructure.map(prj => <ProjectWrapper key={prj.project.projectUuid} project={prj}/>)
            }
            {
                gridStructure.flatMap(prj =>
                    prj.tasks.map(tsk => (
                    <TaskWrapper
                        task={tsk}
                        key={tsk.task.taskUuid}
                    />
                    ))
                )
            }
            <div className="timelineEmptyRow"/>
        </StyledTasksTimeline>
    )
}


const StyledTasksTimeline = styled.div`
    display: grid;
    grid-template-columns: 281px repeat(91, 70px);
    grid-template-rows: 60px repeat(${({$totalRows}) => $totalRows}, 40px);
    border: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
    border-radius: ${({theme}) => theme.cornerRadius.md};

    overflow: auto;

    width: 100%;
    height: 100%;
    position: relative;

   background-image:
  linear-gradient(
    to right,
    transparent calc(((31 - 1) * 70px) + 280px),
    ${({theme}) => theme.colors.neutralTertiaryBackground} calc(((31 - 1) * 70px) + 280px),
    ${({theme}) => theme.colors.neutralTertiaryBackground} calc((31 * 70px) + 280px),
    transparent calc((31 * 70px) + 280px)
  ),

  repeating-linear-gradient(
    to right,
    ${({theme}) => theme.colors.neutralTertiaryBackground} 0px,
    ${({theme}) => theme.colors.neutralTertiaryBackground} 1px,
    transparent 1px,
    transparent 70px
  );

    background-attachment: local;

    div.timelineEmpty
    {
        grid-row-start: 1;
        grid-row-end: 2;
        grid-column-start: 1;
        grid-column-end: 1;
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        border-right: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        background: ${({theme}) => theme.colors.neutralSecondaryBackground};
        position: sticky;
        top: 0;
        left: 0;
        z-index: 100;
    }

    div.timelineEmptyRow
    {

        border-right: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        background: ${({theme}) => theme.colors.neutralSecondaryBackground};
        position: sticky;
        left: 0;
        grid-column: 1 / span 1;
        grid-row: -1 / span 1;
    }

    div.timelineDate
    {

        &:first-of-type
        {
            grid-column-start: 2;
            border: 1px solid red;
        }

        position: sticky;
        top: 0;
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${({theme}) => theme.spacing.xs};
        justify-content: center;

        span.month
        {
            text-transform: uppercase;
            color: ${({theme}) => theme.colors.mutedForeground};
            font-size: ${({theme}) => theme.fontSize.sm};
            opacity: 0.5;
        }

        span.day
        {
            text-transform: uppercase;
            color: ${({theme}) => theme.colors.mutedForeground};
            font-size: ${({theme}) => theme.fontSize.sm};

            &.weekend
            {
                color: ${({theme}) => theme.colors.errorForeground}; 
            }
        }

        span.date
        {
            font-weight: 600;
        }
    }
`

const ProjectWrapper = ({project}) => {

    return (
        <>
             <StyledProjectWrapper
                style={{
                    gridRow: project.grid.rowStart + "/" + (project.grid.rowEnd + 1),
                    gridColumn: project.grid.columnStart + "/" + project.grid.columnEnd,
                }}
             >
                <MaterialIcon name='folder_open' weight={300}/>
                <div className="projectName">
                    {project.project.projectName}
                </div>
            </StyledProjectWrapper>
        </>
       
    )
}

const StyledProjectWrapper = styled.div`
    border-right: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
    border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
    background: ${({theme}) => theme.colors.neutralSecondaryBackground};
    position: sticky;
    left: 0;
    z-index: 500;

    display: flex;
    align-items: center;
    gap: ${({theme}) => theme.spacing.md};
    padding: ${({theme}) => theme.spacing.md};
    font-weight: 500;

    svg
    {
        height: 18px;
        width: 18px;
        fill: ${({theme}) => theme.colors.neutralSecondaryForeground};
    }
`

const TaskWrapper = ({task}) => {
    const navigate = useNavigate();
    return (
        <StyledTaskWrapper
            onDoubleClick={() => navigate("/tasks/" + task.task.taskUuid)}
            style={{
                gridRow: (task.grid.rowStart) + " / " + (task.grid.rowEnd + 1),
                gridColumn: (task.grid.columnStart) + " / " + (task.grid.columnEnd)
            }}
            $prioriy = {task.task.taskPriority}
            className={task.task.taskStatus === "Finished" ? 'taskFinished' : null}
            title={task.task.taskTitle}
        >
            <div className="content hiddenScrollbar">
                <div className="contentWrapper">
                    <div className="title">{task.task.taskTitle}</div>
                    <div className="duration">{parseDuration(task.task.taskDuration)}</div>
                </div>
            </div>
        </StyledTaskWrapper>
    )
}

const StyledTaskWrapper = styled.div`
    padding: ${({theme}) => theme.spacing.xs} ${({theme}) => theme.spacing.sm};
    cursor: pointer;
    filter: drop-shadow(0 2px 2px rgba(${({theme}) => theme.colors.neutralSecondaryForegroundRGB}, 0.15));

    &:hover
    {
        min-width: fit-content;
    }
    

    &.taskFinished
    {
        opacity: 0.65;
    }
    
    > div.content
    {
        border-radius: ${({theme}) => theme.cornerRadius.md};
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        
        padding: ${({theme}) => theme.spacing.none} ${({theme}) => theme.spacing.md};

        border: 1px solid ${({theme, $prioriy}) => $prioriy === 1 ? theme.colors.successForeground : $prioriy === 2 ? theme.colors.warningForeground : theme.colors.errorForeground };
        background: ${({theme, $prioriy}) => $prioriy === 1 ? theme.colors.successBackground : $prioriy === 2 ? theme.colors.warningBackground : theme.colors.errorBackground };
        
        > div.contentWrapper
        {
            max-width: fit-content;
            position: sticky;
            left: calc(281px + ${({theme}) => theme.spacing.md});

            > div.title
            {
                font-weight: 500;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
            }
            
            > div.duration
            {
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
            }
        }
        
    }
`

const generateTimelineStructure = (projects, tasks) => {

    const debug = true;

    const today = new Date();

    const normalize = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const firstDay = normalize(new Date(today));
    firstDay.setDate(firstDay.getDate() - 30);

    const lastDay = normalize(new Date(today));
    lastDay.setDate(lastDay.getDate() + 60);

    const DAY_MS = 1000 * 60 * 60 * 24;

    const dateToColumn = (date) => {
        const d = normalize(date);
        const diff = Math.floor((d - firstDay) / DAY_MS);
        return diff + 2;
    };

    const safeProjects = (projects || []).filter(Boolean);
    const safeTasks = (tasks || []).filter(Boolean);

    const getProjectTasks = (projectUuid) =>
        safeTasks.filter(t =>
            t?.taskProjectUuid === projectUuid
        );

    const groupTasks = (projectTasks) => {

        const normalizedTasks = (projectTasks || [])
            .filter(Boolean)
            .map(t => ({
                task: t,
                start: normalize(t.taskStart),
                end: normalize(t.taskDeadline)
            }))
            .filter(t =>
                t.start instanceof Date &&
                t.end instanceof Date &&
                !isNaN(t.start) &&
                !isNaN(t.end)
            )
            .sort((a, b) => a.start - b.start);

        const lanes = [];

        for (const item of normalizedTasks) {

            if (!item?.task) continue;

            let placed = false;

            for (const lane of lanes) {
                const last = lane[lane.length - 1];

                if (last.end < item.start) {
                    lane.push(item);
                    placed = true;
                    break;
                }
            }

            if (!placed) lanes.push([item]);
        }

        return lanes;
    };

    let currentRow = 2;

    return safeProjects
        .filter(p => p?.projectUuid)
        .map(prj => {

            const projectTasks = getProjectTasks(prj.projectUuid);
            const lanes = groupTasks(projectTasks);

            if (!lanes || lanes.length === 0 || lanes.every(l => !l?.length)) {
                return null;
            }

            const projectRowStart = currentRow;
            const projectRowEnd = projectRowStart + Math.max(lanes.length - 1, 0);

            currentRow = projectRowEnd + 1;

            const tasksWithLayout = lanes
                .filter(Boolean)
                .flatMap((lane, laneIndex) => {

                    const row = projectRowStart + laneIndex;

                    return (lane || [])
                        .filter(Boolean)
                        .map(item => {

                            if (!item?.task) return null;

                            if (item.end < firstDay || item.start > lastDay) return null;

                            const clippedStart = item.start < firstDay ? firstDay : item.start;
                            const clippedEnd = item.end > lastDay ? lastDay : item.end;

                            return {
                                task: item.task,
                                grid: {
                                    rowStart: row,
                                    rowEnd: row,
                                    columnStart: dateToColumn(clippedStart),
                                    columnEnd: dateToColumn(clippedEnd) + 1
                                }
                            };
                        })
                        .filter(Boolean);
                });

            const result = {
                project: prj,
                grid: {
                    rowStart: projectRowStart,
                    rowEnd: projectRowEnd,
                    columnStart: 1,
                    columnEnd: 2
                },
                tasks: tasksWithLayout
            };

            if (debug) {
                result._debug = {
                    lanesRaw: lanes,
                    tasksFlat: lanes.flat(),
                    taskCount: projectTasks.length,
                    laneCount: lanes.length
                };
            }

            return result;
        })
        .filter(Boolean);
};
